import isElectron from 'is-electron';
import videoStreamProcessorManager from './video-stream-processor-manager';
import {
    VIDEO_RESOLUTION,
    EFFECT_MODE,
    CANVAS_SETTINGS,
} from 'flow/constants/video-background-constants';

/**
 * Get video resolution constraints based on configuration
 * @returns {Object} Resolution constraints with width and height
 */
const getResolutionConstraints = () => {
    const resolution = process.env.VIDEO_RESOLUTION;

    // HD: 1280x720
    if (resolution === 'HD' || resolution === 'hd') {
        return VIDEO_RESOLUTION.HD;
    }

    // Default: 640x360
    return VIDEO_RESOLUTION.DEFAULT;
};

export const stopStream = async (stream) => {
    const waitedStream = await stream;
    if (waitedStream && waitedStream.getTracks) {
        waitedStream.getTracks().forEach((track) => track.stop());
    }
};

/**
 * Stop stream and clean up background processor if active
 * @param {MediaStream} stream - Stream to stop
 */
export const stopStreamAndCleanup = async (stream) => {
    await stopStream(stream);
    // Only clean up the processor if it's currently managing this exact stream.
    // This prevents stale stop calls (e.g. from the lobby cleanup after the meeting
    // processor has already taken over) from killing the wrong processor instance.
    if (stream && stream === videoStreamProcessorManager.getProcessedStream()) {
        await videoStreamProcessorManager.cleanup();
    }
};

/**
 * Get local video stream from camera
 * @param {string} deviceId - Camera device ID
 * @returns {Promise<MediaStream>} Raw camera stream
 */
export const getLocalStream = async (deviceId) => {
    const { width, height } = getResolutionConstraints();

    if (navigator.mediaDevices.getUserMedia) {
        try {
            const constraints = {
                video: {
                    width,
                    height,
                    frameRate: { ideal: CANVAS_SETTINGS.FRAME_RATE },
                    deviceId: deviceId ? { exact: deviceId } : undefined,
                },
                audio: false,
            };

            const stream =
                await navigator.mediaDevices.getUserMedia(constraints);

            return stream;
        } catch (e) {
            console.error('Error setting up local video feed:', e);
            throw e;
        }
    } else {
        throw new Error('Could not use mediaDevices.getUserMedia');
    }
};

/**
 * Get local video stream with optional background effects processing
 * @param {string} deviceId - Camera device ID
 * @param {Object} options - Processing options
 * @param {number} options.blurAmount - Blur intensity (5-100 pixels)
 * @param {string} options.mode - Effect mode: 'none', 'blur', 'image'
 * @param {string} options.backgroundImageUrl - URL for background image (if mode is 'image')
 * @returns {Promise<MediaStream>} Processed or raw camera stream
 */
export const getProcessedLocalStream = async (deviceId, options = {}) => {
    const {
        blurAmount,
        mode = EFFECT_MODE.NONE,
        backgroundImageUrl = null,
    } = options;

    // Get raw camera stream
    const rawStream = await getLocalStream(deviceId);

    // Return raw stream if no effects are enabled
    if (mode === EFFECT_MODE.NONE) {
        return rawStream;
    }

    console.info('[VideoUtils] Applying background effects...');

    // Apply background effects processing
    try {
        const processedStream = await videoStreamProcessorManager.processStream(
            rawStream,
            blurAmount,
            mode,
            backgroundImageUrl,
        );
        console.info(
            '[VideoUtils] Processed stream obtained:',
            processedStream,
        );
        return processedStream;
    } catch (error) {
        console.error(
            '[VideoUtils] Background effects processing failed, using original stream:',
            error,
        );
        // Graceful fallback to original stream
        return rawStream;
    }
};

export const getScreenShareStream = async (screenId) => {
    if (isElectron()) {
        try {
            return await getElectronScreenStream(screenId);
        } catch (e) {
            console.error('Error getting screen stream for electron: ', e);
            // TODO: #error-handling Deal with the error
            return undefined;
        }
    } else {
        try {
            return await getScreenStream();
        } catch (e) {
            if (e.name === 'NotAllowedError') {
                //todo deal with no access to screen recording, meaning need access rights
                return undefined;
            } else if (e instanceof NoDisplayMediaError) {
                //todo deal with browser doesn't support getDisplayMedia-function
                return undefined;
            } else {
                //todo deal with other potential errors
                return undefined;
            }
        }
    }
};

/**
 * Whether screen sharing can work in the current runtime. Electron drives its
 * own desktop capturer, while the web path needs `getDisplayMedia` — which no
 * mobile browser or webview (iOS Safari/WKWebView, Android WebView) exposes, so
 * the control is hidden there rather than offered as a button that always fails.
 */
export const screenShareIsSupported = () =>
    isElectron() || !!navigator.mediaDevices?.getDisplayMedia;

export const getScreenStream = async () => {
    if (navigator.mediaDevices.getDisplayMedia) {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
            });
            return stream;
        } catch (e) {
            console.error('Error setting up screen share video feed:', e);
            throw e;
        }
    } else {
        throw new NoDisplayMediaError();
    }
};

export function NoDisplayMediaError() {
    this.name = 'NoDisplayMediaError';
    this.message =
        'Could not use mediaDevices.getDisplayMedia to setup screen recording stream';
}

export const getElectronScreenStream = async (chromeMediaSourceId) => {
    if (!isElectron() || !chromeMediaSourceId) {
        return false;
    }
    try {
        return await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                mandatory: {
                    chromeMediaSource: 'desktop',
                    chromeMediaSourceId,
                },
            },
        });
    } catch (e) {
        console.error(
            'Error in getting stream from screen, getElectronScreenStream:',
            e,
        );
        // TODO: #error-handling Deal with the error
        throw e;
    }
};
